import React from 'react';
import { usePageSEO } from '@/hooks/usePageSEO';
import { PUBLIC_PAGES } from '@/lib/seo/public-routes';
import { pageMetadata } from '@/lib/seo/metadata';
import { changelogEntries } from '@/lib/changelog';
import { PublicContainer } from '@/components/layouts/PublicContainer';

const PROSE_CLASSES = [
  '[&_h2]:mt-6 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2]:text-foreground',
  '[&_h3]:mt-5 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:tracking-tight [&_h3]:text-foreground',
  '[&_p]:mt-3 [&_p]:text-[15px] [&_p]:leading-relaxed [&_p]:text-muted-foreground',
  '[&_ul]:mt-3 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5 [&_ul]:text-[15px] [&_ul]:leading-relaxed [&_ul]:text-muted-foreground',
  '[&_ol]:mt-3 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:pl-5 [&_ol]:text-[15px] [&_ol]:leading-relaxed [&_ol]:text-muted-foreground',
  '[&_a]:text-lime [&_a]:underline [&_a]:underline-offset-4',
  '[&_strong]:font-semibold [&_strong]:text-foreground',
  '[&_code]:mono [&_code]:rounded [&_code]:bg-surface-2 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[13px] [&_code]:text-foreground',
].join(' ');

function formatDate(date: string) {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

const Changelog: React.FC = () => {
  usePageSEO(pageMetadata(PUBLIC_PAGES.changelog));

  return (
    <div className="bg-background py-24 sm:py-32">
      <PublicContainer>
        <div className="mx-auto max-w-3xl">
          <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Changelog</h1>
          <p className="mt-2 text-lg leading-8 text-muted-foreground">
            New features, fixes, and improvements — most recent first.
          </p>

          <div className="mt-10 space-y-10">
            {changelogEntries.length === 0 && (
              <p className="text-sm text-muted-foreground">Nothing here yet — check back soon.</p>
            )}
            {changelogEntries.map(({ frontmatter, Component }) => (
              <div key={frontmatter.version} className="rounded-xl border border-hairline bg-surface-1 p-6">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="mono inline-flex items-center rounded-full border border-lime/30 bg-lime/10 px-2.5 py-0.5 text-[11px] font-bold text-lime">
                    {frontmatter.version}
                  </span>
                  <span className="mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    {formatDate(frontmatter.date)}
                  </span>
                </div>
                <h2 className="mt-3 text-xl font-semibold tracking-tight text-foreground">{frontmatter.title}</h2>
                <article className={PROSE_CLASSES}>
                  <Component />
                </article>
              </div>
            ))}
          </div>
        </div>
      </PublicContainer>
    </div>
  );
};

export default Changelog;
